import {
  Flex,
  Text,
  useColorModeValue,
  useDisclosure,
  Alert,
  AlertIcon,
  Spinner,
} from "@chakra-ui/react";
import * as React from "react";

import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";

// Custom components
import Card from "components/card/Card";
import ClientCombobox from "components/list/ClientCombobox";
import ListTable, { LIST_MUTED_COLOR, ListCheck, ListHeader, ListRow } from "components/list/ListTable";
import { usePagination } from "components/list/TablePagination";
import { useListState } from "hooks/useListState";
import ModalForm from "components/modal/modalForm";
import Moment from "react-moment";
import FieldClient from "components/user/FieldClient";
import { numberToHour } from "utils";
import ListMenuIntervention from "components/menu/ListMenuIntervention";
import { useClient } from "../../../../hooks/useClient";
import { useForfait } from "hooks/useForfait";
import ModalEmail from "components/modal/modalEmail";
import { useHistory, useLocation } from "react-router-dom";
import ModalDelete from "components/modal/modalDelete";
import { isEmailPending } from "views/admin/default/dashboardData";

type TableInterventionType = {
  id: string;
  date_intervention: any;
  cms: string;
  envoi_email: boolean;
  idforfait: string;
  plugins: boolean;
  sauvegarde: boolean;
  site_internet: string;
  temps_passe: number;
  client: string;
};

const columnHelper = createColumnHelper<TableInterventionType>();

// Email au client. Seuls ceux de la période récente sont signalés, comme sur le dashboard :
// un ancien email jamais envoyé n'est plus une alerte
function EmailStatus(props: { sent: boolean; date: Date }) {
  const { sent, date } = props;
  const pendingColor = useColorModeValue("orange.600", "orange.300");
  if (sent) {
    return <ListCheck checked />;
  }
  return isEmailPending(sent, date, new Date()) ? (
    <Text as="span" color={pendingColor} fontWeight="700">
      À envoyer
    </Text>
  ) : (
    <Text as="span" color={LIST_MUTED_COLOR}>
      Non envoyé
    </Text>
  );
}

// const columns = columnsDataCheck;
export default function ColumnTable(props: { tableData: any }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const modalDelete = useDisclosure();
  const modalEmail = useDisclosure();
  const { tableData } = props;
  const [sorting, setSorting] = useListState<SortingState>("interventions:tri", [
    { id: "date_intervention", desc: true },
  ]);
  const [selectedIntervention, setSelectedIntervention] = React.useState<
    InterventionType[] | null
  >(null);
  let defaultData = tableData;

  // Colonnes créées une seule fois : de nouvelles fonctions `cell` à chaque rendu
  // feraient remonter toutes les cellules (et relancer leurs lectures Firestore)
  const columns = React.useMemo(
    () => [
      columnHelper.accessor("client", {
        id: "client",
        header: "Client",
        // Le nom est lu cellule par cellule : il n'y a pas de valeur à trier
        enableSorting: false,
        cell: (info) => (
          <>
            <Text fontWeight="700">
              <FieldClient
                id={info.row.original.idforfait}
                field="raison_sociale"
                context="intervention"
              />
            </Text>
            <Text color={LIST_MUTED_COLOR} fontSize="xs" lineHeight="16px">
              {info.row.original.site_internet}
            </Text>
          </>
        ),
      }),
      columnHelper.accessor("date_intervention", {
        id: "date_intervention",
        header: "Date",
        meta: { width: { base: "110px", "2xl": "13%" } },
        cell: (info) => <Moment format="DD/MM/YYYY">{info.getValue().toDate()}</Moment>,
      }),
      columnHelper.accessor("temps_passe", {
        id: "temps_passe",
        header: "Temps",
        meta: { width: { base: "80px", "2xl": "10%" } },
        cell: (info) => (
          <Text as="span" fontWeight="700" sx={{ fontVariantNumeric: "tabular-nums" }}>
            {numberToHour(info.getValue())}
          </Text>
        ),
      }),
      columnHelper.accessor("cms", {
        id: "cms",
        header: "CMS",
        meta: { width: { base: "130px", "2xl": "14%" } },
      }),
      columnHelper.accessor("plugins", {
        id: "plugin",
        header: "Plugin",
        meta: { width: { base: "80px", "2xl": "10%" } },
        cell: (info) => <ListCheck checked={info.getValue()} />,
      }),
      columnHelper.accessor("sauvegarde", {
        id: "sauvegarde",
        header: "Sauvegarde",
        meta: { width: { base: "110px", "2xl": "11%" } },
        cell: (info) => <ListCheck checked={info.getValue()} />,
      }),
      columnHelper.accessor("envoi_email", {
        id: "envoi_email",
        header: "Email client",
        meta: { width: { base: "125px", "2xl": "14%" } },
        cell: (info) => (
          <EmailStatus
            sent={info.getValue()}
            date={info.row.original.date_intervention.toDate()}
          />
        ),
      }),
      columnHelper.display({
        id: "actions",
        meta: { width: "67px", align: "right" },
        cell: (info) => (
          <ListMenuIntervention
            interventionrow={[info.row.original]}
            openmodal={onOpen}
            setselectedintervention={setSelectedIntervention}
            opendeletemodal={modalDelete.onOpen}
            openemailmodal={modalEmail.onOpen}
          />
        ),
      }),
    ],
    [onOpen, modalDelete.onOpen, modalEmail.onOpen]
  );
  const { getClients } = useClient();
  const { getClientForfaits } = useForfait();
  const [clientsFilter, setClientsFilter] = React.useState([]);
  const [selectedClient, setSelectedClient] = useListState("interventions:client", "");
  // Forfaits du client sélectionné (null = pas de filtre client). Pendant leur chargement ou
  // après une erreur, le tableau reste vide plutôt que d'afficher les interventions d'un autre filtre
  const [clientFilter, setClientFilter] = React.useState<{
    status: "loading" | "error" | "ready";
    forfaitIds: Set<string>;
  } | null>(() => (selectedClient ? { status: "loading", forfaitIds: new Set() } : null));

  // Filtre par forfait via l'URL (lien "Voir les interventions" depuis un forfait)
  const location = useLocation();
  const history = useHistory();
  const idForfait = new URLSearchParams(location.search).get("idforfait");

  // Liste complète des interventions ; les filtres sont appliqués à l'affichage,
  // ce qui les conserve après un ajout, une modification, un envoi ou une suppression
  const [allData, setAllData] = React.useState<any[]>(() => [...defaultData]);
  const data = React.useMemo(
    () =>
      allData.filter(
        (intervention: { idforfait: string }) =>
          (!idForfait || intervention.idforfait === idForfait) &&
          (!clientFilter || clientFilter.forfaitIds.has(intervention.idforfait))
      ),
    [allData, idForfait, clientFilter]
  );

  const [pagination, setPagination] = usePagination(data.length);
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: (updater) => {
      setSorting(updater);
      table.setPageIndex(0);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // Lignes identifiées par leur document : après un tri ou un changement de page,
    // chaque cellule garde son enregistrement (et ne relance pas ses lectures Firestore)
    getRowId: (row) => row.id,
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    // Garde la page après une modification ; le tri, les filtres et les ajouts reviennent à la première
    autoResetPageIndex: false,
  });
  // Un ajout revient à la première page, où le tri par défaut place la nouvelle ligne
  function handleFormData(newData: any[]) {
    setAllData(newData);
    if (!selectedIntervention) {
      table.setPageIndex(0);
    }
  }
  function handleAdd() {
    setSelectedIntervention(null);
    onOpen();
  }
  function handleChangeClient(id: string) {
    setSelectedClient(id);
    table.setPageIndex(0);
    // Le filtre client remplace le filtre par forfait venant de l'URL
    if (idForfait) {
      history.replace(location.pathname);
    }
  }
  // Un lien vers les interventions d'un forfait remplace le filtre client gardé
  React.useEffect(() => {
    if (idForfait) {
      setSelectedClient("");
    }
  }, [idForfait, setSelectedClient]);
  // Charge les forfaits du client sélectionné, y compris quand le filtre est restauré
  React.useEffect(() => {
    if (!selectedClient) {
      setClientFilter(null);
      return;
    }
    let cancelled = false;
    setClientFilter({ status: "loading", forfaitIds: new Set() });
    getClientForfaits(selectedClient)
      .then((forfaits) => {
        if (!cancelled) {
          setClientFilter({
            status: "ready",
            forfaitIds: new Set((forfaits || []).map((forfait) => forfait.id)),
          });
        }
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) {
          setClientFilter({ status: "error", forfaitIds: new Set() });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedClient, getClientForfaits]);
  React.useEffect(() => {
    const fetchClients = async () => {
      try {
        const clientsData = await getClients();
        setClientsFilter(clientsData);
        // Client gardé en filtre puis supprimé : le filtre est abandonné
        setSelectedClient((current) =>
          current && !clientsData.some((client) => client.id === current) ? "" : current
        );
      } catch (error) {
        console.error("Erreur lors de la récupération des clients :", error);
      }
    };
    fetchClients();
  }, [getClients, setSelectedClient]);
  return (
    <Card flexDirection="column" w="100%" px="0px" pt="16px" pb="8px">
      <ListHeader
        title={`Liste des Interventions (${data.length})`}
        addLabel="Ajouter une intervention"
        onAdd={handleAdd}
      >
        <ClientCombobox clients={clientsFilter} value={selectedClient} onChange={handleChangeClient} />
      </ListHeader>
      {clientFilter?.status === "loading" && (
        <Flex justify="center" py="20px">
          <Spinner />
        </Flex>
      )}
      {clientFilter?.status === "error" && (
        <Alert status="error" mx="25px" w="auto" borderRadius="md">
          <AlertIcon />
          Impossible de charger les forfaits de ce client. Choisissez un autre client ou rechargez la page.
        </Alert>
      )}
      <ListTable
        table={table}
        renderRow={(row) => <ListRow key={row.id} row={row} />}
      />
      <ModalForm<InterventionType>
        isOpen={isOpen}
        onClose={onClose}
        newData={handleFormData}
        updateData={selectedIntervention}
        form="intervention"
      />
      {selectedIntervention && (
        <ModalEmail
          isOpen={modalEmail.isOpen}
          onClose={modalEmail.onClose}
          intervention={selectedIntervention[0]}
          newdata={setAllData}
        />
      )}
      {selectedIntervention && (
        <ModalDelete
          isOpen={modalDelete.isOpen}
          onClose={modalDelete.onClose}
          idDelete={selectedIntervention[0].id}
          field={selectedIntervention[0].site_internet}
          type={"intervention"}
          newdata={setAllData}
        />
      )}
    </Card>
  );
}
